/**
 * Normalize BCP 47 tag to primary language subtag: "uk-UA" → "uk", "en_US" → "en".
 */
export function normalizeLocaleTag(tag: string): string {
  const trimmed = tag.trim().toLowerCase();
  if (!trimmed) return '';
  return trimmed.split(/[-_]/)[0] ?? '';
}

/** Widget UI ships only `uk` and `en`; site `ru` maps to Ukrainian. */
export function toWidgetLocale(tag: string): 'uk' | 'en' {
  const base = normalizeLocaleTag(tag);
  if (base === 'uk' || base === 'ru') return 'uk';
  return 'en';
}

/**
 * Collect locale hints from the host page (priority: html[lang] → navigator).
 */
export function collectSiteLocaleTags(): string[] {
  const tags: string[] = [];

  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang?.trim();
    if (htmlLang) tags.push(htmlLang);
  }

  if (typeof navigator !== 'undefined') {
    if (navigator.language) tags.push(navigator.language);
    for (const lang of navigator.languages ?? []) {
      if (lang) tags.push(lang);
    }
  }

  return tags;
}

export type ResolveSiteLocaleOptions = {
  /** Explicit override (Storybook / tests). Takes precedence over site detection. */
  initialLocale?: string;
};

function readHtmlLang(): string {
  if (typeof document === 'undefined') return '';
  return document.documentElement.lang?.trim() ?? '';
}

function readNavigatorLang(): string {
  if (typeof navigator === 'undefined') return '';
  if (navigator.language) return navigator.language;
  return navigator.languages?.[0] ?? '';
}

/**
 * Pick widget locale from the host page.
 *
 * Product rule: site `uk`/`ru` → widget `uk`; any other site locale → `en`.
 *
 * 1. `initialLocale` (Storybook / tests) — same mapping
 * 2. `<html lang>`
 * 3. `navigator.language`
 * 4. `"en"`
 *
 * `availableLanguages` (`LANGUAGES`) is kept for call-site compatibility and ignored.
 */
export function resolveSiteLocale(
  _availableLanguages: string[] = [],
  options: ResolveSiteLocaleOptions = {},
): string {
  if (options.initialLocale?.trim()) {
    return toWidgetLocale(options.initialLocale);
  }

  const htmlLang = readHtmlLang();
  if (htmlLang) {
    return toWidgetLocale(htmlLang);
  }

  const navLang = readNavigatorLang();
  if (navLang) {
    return toWidgetLocale(navLang);
  }

  return 'en';
}
